import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Twitter, Youtube, Music2, Mail, Heart } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { branding } from '../config/branding';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const { currentTheme } = useTheme();
  const currentYear = new Date().getFullYear();
  const [footerLogo, setFooterLogo] = useState(null);

  useEffect(() => {
    // Load footer logo from backend
    axios.get(`${BACKEND_URL}/api/branding/settings?t=${Date.now()}`)
      .then(response => {
        if (response.data.footer_logo_url) {
          setFooterLogo(`${BACKEND_URL}${response.data.footer_logo_url}?t=${Date.now()}`);
        } else if (response.data.logo_url) {
          // Fallback to main logo if footer logo not set
          setFooterLogo(`${BACKEND_URL}${response.data.logo_url}?t=${Date.now()}`);
        }
      })
      .catch(error => {
        console.log('Using default footer logo');
      });
  }, []);

  const socialLinks = [
    { 
      name: 'Instagram', 
      icon: Instagram, 
      url: branding.social.instagram,
      color: '#E4405F'
    },
    { 
      name: 'Twitter', 
      icon: Twitter, 
      url: branding.social.twitter,
      color: '#1DA1F2'
    },
    { 
      name: 'YouTube', 
      icon: Youtube, 
      url: branding.social.youtube,
      color: '#FF0000'
    },
    { 
      name: 'SoundCloud', 
      icon: Music2, 
      url: branding.social.soundcloud,
      color: '#FF5500'
    }
  ];

  const footerLinks = [
    {
      title: 'Shop',
      links: [
        { label: 'Beat Store', path: '/beats' },
        { label: 'Discography', path: '/discography' },
        { label: 'Merch', path: '/merch' }
      ]
    },
    {
      title: 'Company',
      links: [
        { label: 'About', path: '/' },
        { label: 'Collaborate', path: '/collaborate' },
        { label: 'Contact', path: '/contact' }
      ]
    },
    {
      title: 'Legal',
      links: [
        { label: 'Terms of Service', path: '/terms' },
        { label: 'Privacy Policy', path: '/privacy' },
        { label: 'Licensing', path: '/licensing' }
      ]
    }
  ];

  return (
    <footer className="bg-zinc-950 border-t border-white/10 pb-20 md:pb-0">
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Top Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-8">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center space-x-2 mb-4 group">
              <img 
                src="/logos/tha-advocate-logo.png" 
                alt="Tha Advocate Beats"
                className="h-10 md:h-12 max-w-[180px] md:max-w-[220px] object-contain transition-all duration-300 group-hover:scale-105"
                onError={(e) => {
                  e.target.src = '/logos/marz-symbol.png';
                }}
              />
            </Link>
            <p className="text-gray-400 mb-6 max-w-sm">
              {branding.about.shortBio}
            </p>
            
            {/* Social Icons */}
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-all duration-300 hover:scale-110 group"
                  aria-label={social.name}
                >
                  <social.icon 
                    className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" 
                    style={{ '--hover-color': social.color }}
                  />
                </a>
              ))}
            </div>
          </div>

          {/* Links Columns */}
          {footerLinks.map((section, idx) => (
            <div key={idx}>
              <h3 className="text-white font-bold mb-4">{section.title}</h3>
              <ul className="space-y-2">
                {section.links.map((link, linkIdx) => (
                  <li key={linkIdx}>
                    <Link
                      to={link.path}
                      className="text-gray-400 hover:text-white transition-colors duration-300 text-sm"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter Signup */}
        <div className="py-8 border-t border-white/10 mb-8">
          <div className="max-w-md">
            <h3 className="text-white font-bold mb-2">Stay Updated</h3>
            <p className="text-gray-400 text-sm mb-4">
              Get exclusive beats, production tips, and special offers.
            </p>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all text-sm"
                style={{ '--tw-ring-color': currentTheme.primary }}
              />
              <button
                className="px-6 py-2 rounded-lg font-bold text-black transition-all hover:scale-105 text-sm"
                style={{ backgroundColor: currentTheme.primary }}
              >
                <Mail className="w-4 h-4 inline mr-1" />
                Subscribe
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © {currentYear} {branding.siteName}. All rights reserved.
          </p>
          <p className="text-gray-500 text-sm flex items-center gap-1">
            Made with <Heart className="w-4 h-4 fill-red-500 text-red-500" /> for the culture
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;